/**
 * Turns react-hotkeys-hook key strings into platform-aware display tokens for
 * the shortcuts overlay (and anywhere else we surface a shortcut hint).
 *
 * `"mod+k"` → `["⌘", "K"]` on macOS, `["Ctrl", "K"]` elsewhere.
 * `"shift+slash"` → `["?"]` (the typed glyph reads better than ⇧ + /).
 * `"g c"` (a leader sequence) → `{ sequence: true, tokens: ["G", "C"] }`.
 */

import { isMac } from "./platform.ts";

const mac = isMac();

// Combos we'd rather show as the literal typed character than as modifier glyphs.
const LITERAL_COMBOS: Record<string, string> = {
  "shift+slash": "?",
};

const TOKEN_LABELS: Record<string, string> = {
  mod: mac ? "⌘" : "Ctrl",
  meta: mac ? "⌘" : "Win",
  ctrl: mac ? "⌃" : "Ctrl",
  control: mac ? "⌃" : "Ctrl",
  alt: mac ? "⌥" : "Alt",
  option: mac ? "⌥" : "Alt",
  shift: "⇧",
  enter: "Enter",
  return: "Enter",
  escape: "Esc",
  esc: "Esc",
  backspace: "⌫",
  delete: "Del",
  up: "↑",
  down: "↓",
  left: "←",
  right: "→",
  slash: "/",
  comma: ",",
  period: ".",
  space: "Space",
  tab: "Tab",
  minus: "-",
  equal: "=",
  semicolon: ";",
  quote: "'",
  backquote: "`",
  backslash: "\\",
  bracketleft: "[",
  bracketright: "]",
  home: "Home",
  end: "End",
  pageup: "PgUp",
  pagedown: "PgDn",
};

const labelFor = (token: string): string => {
  const lower = token.toLowerCase();
  if (TOKEN_LABELS[lower]) return TOKEN_LABELS[lower];
  // Single letters/digits and function keys render uppercased; anything else as-is.
  return token.length === 1 || /^f\d{1,2}$/.test(lower) ? token.toUpperCase() : token;
};

export interface FormattedCombo {
  /** True when this is a leader sequence ("g then c") rather than a chord. */
  sequence: boolean;
  /** Display tokens, in order. */
  tokens: string[];
}

/** Format a single key combo string into display tokens. */
export const formatCombo = (combo: string): FormattedCombo => {
  const literal = LITERAL_COMBOS[combo.toLowerCase()];
  if (literal) return { sequence: false, tokens: [literal] };

  // Space-separated => leader sequence (g, then c). Each step is a single key.
  if (combo.includes(" ")) {
    return { sequence: true, tokens: combo.split(/\s+/).map(labelFor) };
  }

  return { sequence: false, tokens: combo.split("+").map(labelFor) };
};

/** Format every combo bound to a shortcut (primary first). */
export const formatCombos = (combos: string[]): FormattedCombo[] => combos.map(formatCombo);
